import type { components } from '@lifeserver/models/iapv2';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export type EmailAuditItem = components['schemas']['EmailAuditDTO'];

type EmailAuditsResponse = components['schemas']['EmailAuditsDTO'];

const QUERY_KEY = 'email-audit';
const PAGE_LIMIT = 25;

type EmailAuditQuery = {
    email?: string;
    orderId?: string;
    cursor?: string;
};

async function fetchEmailAudits({
    email,
    orderId,
    cursor,
}: EmailAuditQuery): Promise<EmailAuditsResponse> {
    const { data } = await axios.get<EmailAuditsResponse>('/api/email-audit', {
        params: { email, orderId, cursor, limit: PAGE_LIMIT },
    });
    return data;
}

export function useEmailAudits({ email, orderId, cursor }: EmailAuditQuery) {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [
            QUERY_KEY,
            email ?? '',
            orderId ?? '',
            cursor ?? '',
            PAGE_LIMIT,
        ],
        queryFn: () => fetchEmailAudits({ email, orderId, cursor }),
    });

    return {
        emailAudits: data?.emailAudits ?? [],
        nextCursor: data?.nextCursor ?? null,
        isLoading,
        error,
        refetch,
    };
}

export function useEmailAuditItem(id: string | undefined) {
    const { data, isLoading, error } = useQuery({
        queryKey: [QUERY_KEY, id],
        queryFn: async () => {
            const { data } = await axios.get<EmailAuditItem>(
                `/api/email-audit/${id}`,
            );
            return data;
        },
        enabled: Boolean(id),
    });

    return { emailAuditItem: data, isLoading, error };
}
